import React, { useCallback, useMemo, useState } from "react";
import { AccountGroup } from "@common/types/domains/accounting/account";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { FilterPickerList } from "@components/pageHeader/filters/FilterPickerList/FilterPickerList";
import { CoaAccountTreeNode, isCoaAccountNodeMatch } from "./coaAccountTree";
import {
    AccountWithEntity,
    useCoaAccountGrouping,
} from "./useCoaAccountGrouping";
import styles from "./CoaAccountList.module.scss";

type SlotRenderer =
    | React.ReactNode
    | ((ctx: { search: string }) => React.ReactNode);

function renderSlot(slot: SlotRenderer | undefined, search: string) {
    return typeof slot === "function" ? slot({ search }) : slot;
}

const toAccountItem = (node: CoaAccountTreeNode): PickerItemData<string> => ({
    value: node.id,
    label: node.label,
    children: node.children.map(toAccountItem),
});

const collectNodes = (
    nodes: readonly CoaAccountTreeNode[],
): CoaAccountTreeNode[] =>
    nodes.flatMap((node) => [node, ...collectNodes(node.children)]);

const collectLeaves = (
    nodes: readonly CoaAccountTreeNode[],
): CoaAccountTreeNode[] =>
    collectNodes(nodes).filter((node) => node.children.length === 0);

interface CoaAccountListProps {
    accounts: AccountWithEntity[] | undefined;
    groups: AccountGroup[] | undefined;
    entityIds?: number[];
    selectedIds: string[] | undefined;
    onSelectedIdsChange: (nextSelectedIds: string[] | undefined) => void;
    /** Rendered above the search and the account groups. */
    headerSlot?: SlotRenderer;
    /** Rendered below the account groups and their actions footer. */
    footerSlot?: SlotRenderer;
    /** Added to `selectedIds.length` when deciding whether to show the actions footer and what count to display. */
    extraSelectedCount?: number;
    /** Called when the user clicks "Deselect all" in the actions footer. */
    onClearAll: () => void;
}

export const CoaAccountList: React.FC<CoaAccountListProps> = ({
    accounts,
    groups,
    entityIds,
    selectedIds,
    onSelectedIdsChange,
    headerSlot,
    footerSlot,
    extraSelectedCount = 0,
    onClearAll,
}) => {
    const [search, setSearch] = useState("");
    const { accountTree, displayedAccountTree } = useCoaAccountGrouping({
        accounts,
        groups,
        entityIds,
        search,
    });

    // Rows are the displayed tree, so a group ticked while searching selects
    // only the accounts on screen. A leaf row is one account name that can
    // span several entities' account ids; it is a value once all are selected.
    const items = useMemo(
        () => displayedAccountTree.map(toAccountItem),
        [displayedAccountTree],
    );
    const nodesById = useMemo(
        () => new Map(collectNodes(accountTree).map((node) => [node.id, node])),
        [accountTree],
    );
    const selectedIdSet = useMemo(
        () => new Set(selectedIds ?? []),
        [selectedIds],
    );
    const selectedLeafIds = useMemo(
        () =>
            collectLeaves(displayedAccountTree)
                .filter(
                    (node) =>
                        node.accountIds.length > 0 &&
                        node.accountIds.every((id) => selectedIdSet.has(id)),
                )
                .map((node) => node.id),
        [displayedAccountTree, selectedIdSet],
    );

    const handleValueChange = useCallback(
        (nextLeafIds: string[]) => {
            const nextLeafIdSet = new Set(nextLeafIds);
            const previousLeafIdSet = new Set(selectedLeafIds);
            const nextSelectedIdSet = new Set(selectedIds ?? []);
            for (const leafId of selectedLeafIds) {
                if (!nextLeafIdSet.has(leafId)) {
                    nodesById
                        .get(leafId)
                        ?.accountIds.forEach((id) =>
                            nextSelectedIdSet.delete(id),
                        );
                }
            }
            for (const leafId of nextLeafIds) {
                if (!previousLeafIdSet.has(leafId)) {
                    nodesById
                        .get(leafId)
                        ?.accountIds.forEach((id) => nextSelectedIdSet.add(id));
                }
            }
            onSelectedIdsChange([...nextSelectedIdSet]);
        },
        [nodesById, onSelectedIdsChange, selectedIds, selectedLeafIds],
    );

    const matchesSearch = useCallback(
        (item: PickerItemData<string>, query: string) => {
            const node = nodesById.get(item.value);
            return node !== undefined && isCoaAccountNodeMatch(node, query);
        },
        [nodesById],
    );

    return (
        <div className={styles.filter}>
            {renderSlot(headerSlot, search)}
            <div className={styles.list}>
                <FilterPickerList
                    items={items}
                    value={selectedLeafIds}
                    onValueChange={handleValueChange}
                    parentSelection="cascade"
                    filter={matchesSearch}
                    onInputValueChange={setSearch}
                    emptyLabel={
                        search
                            ? "No matching accounts"
                            : "No account codes found"
                    }
                    selectedCount={
                        (selectedIds?.length ?? 0) + extraSelectedCount
                    }
                    onDeselect={onClearAll}
                />
            </div>
            {renderSlot(footerSlot, search)}
        </div>
    );
};
